import React, { useEffect, useState, useMemo } from 'react';
import { Building2, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { Heading } from '../layout';
import { OrganizationsForm, serializeOrganizationChannels } from '../forms/OrganizationsForm';
import { ContactsForm } from '../forms/ContactsForm';
import { TabNavigation } from '../reusable';
import API_BASE_URL from '../../config/api';
import { useBusinessUnitApi } from '../../contexts/BusinessUnitContext';
import Table3 from '../organizations/Table3';
import ContactsTable3 from './ContactsTable3';
import OrganizationDetailModal from './OrganizationDetailModal';
import OrganizationsBulkImportModal, {
  downloadOrganizationsExcelTemplate,
} from './OrganizationsBulkImportModal';
import RegionSegmentStackedChart, {
  SEGMENT_STACK_DIMENSION,
  REGION_ONLY_AXIS_MODES,
} from './RegionSegmentStackedChart';
import MonthlyDistributionChart from './MonthlyDistributionChart';
import { useUIColors } from '../../hooks/useTheme';
import { normalizeProperCaseText } from '../../utils/textUtils';

const formatRut = (value = '') => {
  const clean = String(value || '')
    .toUpperCase()
    .replace(/[^0-9K]/g, '');
  if (clean.length <= 1) return clean;
  const dv = clean.slice(-1);
  const numberPart = clean.slice(0, -1);
  const withDots = numberPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withDots}-${dv}`;
};
const normalizeOrgName = (value = '') =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const EMPTY_ORG_FORM = {
  org_code: 'Se asigna automáticamente',
  org_name: '',
  rut: '',
  segment: 'Sin Asignar',
  channel: '',
  created_by: '',
  assigned_user_id: '',
  website: '',
  linkedin: '',
  industry: '',
  subindustry: '',
  email: '',
  phone: '',
  address: '',
  commune: '',
  region: '',
  country: 'Chile',
  units: [],
  contacts: [],
};

const EMPTY_CONTACT_FORM = {
  contact_code: 'Se asigna automáticamente',
  full_name: '',
  role: '',
  email: '',
  linkedin: '',
  phone: '',
  organization_id: '',
  status: 'Activo',
};

const PERIOD_OPTIONS = [
  { value: 'last_12', label: 'Últimos 12 meses' },
  { value: 'all', label: 'Todos los períodos' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'annual', label: 'Anual' },
  { value: 'current_month', label: 'Mes actual' },
  { value: 'previous_month', label: 'Mes anterior' },
  { value: 'last_3', label: 'Últimos 3 meses' },
  { value: 'last_6', label: 'Últimos 6 meses' },
  { value: 'ytd', label: 'Año en curso' },
];

const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MONTH_OPTIONS = MONTH_LABELS.map((label, index) => ({ value: String(index), label }));

const parseDateSafe = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    const date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
const addMonths = (date, amount) => new Date(date.getFullYear(), date.getMonth() + amount, 1);
const startOfYear = (year) => new Date(year, 0, 1);
const endOfYear = (year) => new Date(year, 11, 31, 23, 59, 59, 999);

const getMonthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const getMonthLabel = (date) => `${MONTH_LABELS[date.getMonth()]} ${date.getFullYear()}`;

const getCreatedDates = (organizations, contacts) =>
  [...organizations, ...contacts].map((row) => parseDateSafe(row.created_at)).filter(Boolean);

const getAvailableYears = (organizations, contacts) => {
  const years = new Set(getCreatedDates(organizations, contacts).map((date) => date.getFullYear()));
  years.add(new Date().getFullYear());
  return Array.from(years).sort((a, b) => a - b);
};

const getMinCreatedDate = (organizations, contacts) => {
  const dates = getCreatedDates(organizations, contacts).sort((a, b) => a - b);
  return dates[0] || new Date();
};

const getMonthsInRange = (start, end) => {
  const months = [];
  let cursor = startOfMonth(start);
  const endCursor = startOfMonth(end);
  while (cursor <= endCursor) {
    months.push({
      key: getMonthKey(cursor),
      label: getMonthLabel(cursor),
      start: startOfMonth(cursor),
      end: endOfMonth(cursor),
    });
    cursor = addMonths(cursor, 1);
  }
  return months;
};

const getYearsAsPeriods = (years) =>
  years.map((year) => ({
    key: `year-${year}`,
    label: String(year),
    start: startOfYear(year),
    end: endOfYear(year),
  }));

const resolvePeriodSelection = ({
  period,
  organizations,
  contacts,
  secondaryYear,
  secondaryMonth,
}) => {
  const now = new Date();
  const currentMonthStart = startOfMonth(now);
  const availableYears = getAvailableYears(organizations, contacts);
  const fallbackYear = availableYears.includes(now.getFullYear())
    ? now.getFullYear()
    : availableYears[availableYears.length - 1] || now.getFullYear();
  const year = Number(secondaryYear) || fallbackYear;
  const monthIndex = Number.isFinite(Number(secondaryMonth)) ? Number(secondaryMonth) : now.getMonth();

  if (period === 'annual') {
    const years =
      secondaryYear === 'all'
        ? availableYears
        : availableYears.includes(year)
          ? [year]
          : [fallbackYear];
    const columns = getYearsAsPeriods(years);
    return {
      mode: 'year',
      range: {
        start: columns[0]?.start || startOfYear(fallbackYear),
        end: columns[columns.length - 1]?.end || endOfYear(fallbackYear),
      },
      columns,
      title: 'Resumen comercial anual',
      subtitle: secondaryYear === 'all'
        ? 'Acumulados por año calendario. Compara años en columnas distintas.'
        : `Acumulado del año calendario ${years[0]}.`,
    };
  }

  if (period === 'monthly') {
    const monthStart = new Date(year, monthIndex, 1);
    const columns = getMonthsInRange(startOfYear(year), endOfYear(year));
    return {
      mode: 'month',
      range: { start: startOfMonth(monthStart), end: endOfMonth(monthStart) },
      columns,
      title: 'Resumen comercial mensual',
      subtitle: `Meses de ${year}. Las tablas inferiores se filtran por ${MONTH_LABELS[monthIndex]} ${year}.`,
    };
  }

  let range;
  if (period === 'all') {
    range = {
      start: startOfMonth(getMinCreatedDate(organizations, contacts)),
      end: endOfMonth(now),
    };
  } else if (period === 'current_month') {
    range = { start: currentMonthStart, end: endOfMonth(now) };
  } else if (period === 'previous_month') {
    const start = addMonths(currentMonthStart, -1);
    range = { start, end: endOfMonth(start) };
  } else if (period === 'last_3') {
    range = { start: addMonths(currentMonthStart, -2), end: endOfMonth(now) };
  } else if (period === 'last_6') {
    range = { start: addMonths(currentMonthStart, -5), end: endOfMonth(now) };
  } else if (period === 'ytd') {
    range = { start: startOfYear(now.getFullYear()), end: endOfMonth(now) };
  } else {
    range = { start: addMonths(currentMonthStart, -11), end: endOfMonth(now) };
  }

  return {
    mode: 'month',
    range,
    columns: getMonthsInRange(range.start, range.end),
    title: 'Resumen comercial mensual',
    subtitle: 'Conteos por fecha de creación. Desplázate horizontalmente si hay más períodos.',
  };
};

const isDateInRange = (value, range) => {
  const date = parseDateSafe(value);
  if (!date) return false;
  return date >= range.start && date <= range.end;
};

const isLead = (organization) => String(organization.status || '').trim().toLowerCase() === 'lead';
const isQualified = (organization) =>
  String(organization.status || '').trim().toLowerCase() === 'calificado';
const isClient = (organization) => String(organization.status || '').trim().toLowerCase() === 'cliente';
const isRecurrent = (organization) =>
  String(organization.status || '').trim().toLowerCase() === 'recurrente';

const formatCount = (value) => Number(value || 0).toLocaleString('es-CL');

const periodSelectStyle = (ui) => ({
  height: '28px',
  borderRadius: '7px',
  border: `1px solid ${ui.inputBorder}`,
  background: ui.inputBg,
  color: ui.inputText || ui.text,
  padding: '0 10px',
  fontSize: '12px',
  fontWeight: 600,
  outline: 'none',
  cursor: 'pointer',
  minWidth: '140px',
  lineHeight: '28px',
});

const periodLabelStyle = (ui) => ({
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: ui.muted,
  lineHeight: 1.1,
});

const LeadsMonthlySummaryTable = ({
  organizations = [],
  contacts = [],
  periods = [],
  title = 'Resumen comercial',
  collapsed = false,
  onToggleCollapsed,
}) => {
  const ui = useUIColors();
  const rows = useMemo(() => {
    const byPeriod = (list, predicate = () => true) =>
      periods.map((period) =>
        list.filter((row) => {
          const date = parseDateSafe(row.created_at);
          return date && date >= period.start && date <= period.end && predicate(row);
        }).length
      );

    const cumulativeByPeriod = (list, predicate = () => true) =>
      periods.map((period) =>
        list.filter((row) => {
          const date = parseDateSafe(row.created_at);
          return date && date <= period.end && predicate(row);
        }).length
      );

    return [
      { label: 'Nuevas Organizaciones', tone: 'neutral', values: byPeriod(organizations) },
      { label: 'Nuevos Contactos', tone: 'neutral', values: byPeriod(contacts) },
      { label: 'Nuevos Leads', tone: 'prospect', values: byPeriod(organizations, isLead) },
      { label: 'Nuevos Calificados', tone: 'qualified', values: byPeriod(organizations, isQualified) },
      { label: 'Nuevos Clientes', tone: 'client', values: byPeriod(organizations, isClient) },
      { label: 'Nuevos Recurrentes', tone: 'recurrent', values: byPeriod(organizations, isRecurrent) },
      { label: 'Total Leads a la fecha', tone: 'total', values: cumulativeByPeriod(organizations, isLead) },
      {
        label: 'Total Calificados a la fecha',
        tone: 'total',
        values: cumulativeByPeriod(organizations, isQualified),
      },
      { label: 'Total Clientes a la fecha', tone: 'total', values: cumulativeByPeriod(organizations, isClient) },
      {
        label: 'Total Recurrentes a la fecha',
        tone: 'total',
        values: cumulativeByPeriod(organizations, isRecurrent),
      },
    ];
  }, [organizations, contacts, periods]);

  const border = ui.border;
  const headerBg = ui.header;
  const text = ui.text;
  const muted = ui.muted;
  const panel = ui.panel;
  const rowBgByTone = {
    neutral: panel,
    prospect: `color-mix(in srgb, ${ui.warning} 14%, ${panel})`,
    qualified: `color-mix(in srgb, #8b5cf6 14%, ${panel})`,
    client: `color-mix(in srgb, ${ui.success} 14%, ${panel})`,
    recurrent: `color-mix(in srgb, ${ui.interactive} 16%, ${panel})`,
    total: `color-mix(in srgb, ${ui.interactive} 12%, ${ui.section})`,
  };
  const cellPad = '4px 8px';

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '10px',
        overflow: 'hidden',
        margin: '10px 0 12px',
        background: panel,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '7px 10px',
          borderBottom: collapsed ? 'none' : `1px solid ${border}`,
          background: headerBg,
        }}
      >
        <h3 style={{ margin: 0, fontSize: '13px', color: text, fontWeight: 700, lineHeight: 1.2 }}>{title}</h3>
        {typeof onToggleCollapsed === 'function' ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-expanded={!collapsed}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              border: `1px solid ${border}`,
              borderRadius: '6px',
              background: panel,
              color: text,
              fontSize: '11px',
              fontWeight: 600,
              padding: '3px 8px',
              cursor: 'pointer',
              lineHeight: 1.2,
            }}
          >
            {collapsed ? 'Mostrar' : 'Ocultar'}
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        ) : null}
      </div>
      {!collapsed ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: Math.max(680, 170 + periods.length * 88), borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: headerBg }}>
                <th
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    padding: cellPad,
                    textAlign: 'left',
                    fontSize: '10px',
                    color: muted,
                    borderRight: `1px solid ${border}`,
                    background: headerBg,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                  }}
                >
                  Métrica
                </th>
                {periods.map((period) => (
                  <th
                    key={period.key}
                    style={{
                      padding: cellPad,
                      textAlign: 'right',
                      fontSize: '10px',
                      color: muted,
                      borderLeft: `1px solid ${border}`,
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                    }}
                  >
                    {period.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} style={{ background: rowBgByTone[row.tone] }}>
                  <td
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      padding: cellPad,
                      borderTop: `1px solid ${border}`,
                      borderRight: `1px solid ${border}`,
                      background: rowBgByTone[row.tone],
                      color: text,
                      fontSize: '11px',
                      fontWeight: row.tone === 'total' ? 800 : 700,
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                    }}
                  >
                    {row.label}
                  </td>
                  {row.values.map((value, index) => (
                    <td
                      key={`${row.label}-${periods[index]?.key || index}`}
                      style={{
                        padding: cellPad,
                        borderTop: `1px solid ${border}`,
                        borderLeft: `1px solid ${border}`,
                        color: text,
                        fontSize: '11px',
                        fontWeight: row.tone === 'total' ? 800 : 600,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.2,
                      }}
                    >
                      {formatCount(value)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

const PaintLeadsWorkspacePlaceholder = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const { authFetch, unit } = useBusinessUnitApi();
  const [usersData, setUsersData] = useState([]);
  const [periodFilter, setPeriodFilter] = useState('last_12');
  const [secondaryYear, setSecondaryYear] = useState('all');
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [organizationsData, setOrganizationsData] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [organizationsError, setOrganizationsError] = useState('');
  const [showOrgFormModal, setShowOrgFormModal] = useState(false);
  const [showOrgBulkImportModal, setShowOrgBulkImportModal] = useState(false);
  const [isImportingOrgs, setIsImportingOrgs] = useState(false);
  const [orgBulkImportResult, setOrgBulkImportResult] = useState(null);
  const [orgBulkImportNotice, setOrgBulkImportNotice] = useState('');
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [isSavingOrg, setIsSavingOrg] = useState(false);
  const [orgFormData, setOrgFormData] = useState(EMPTY_ORG_FORM);
  const [orgFormError, setOrgFormError] = useState('');
  const [orgOriginalContactIds, setOrgOriginalContactIds] = useState([]);
  const [orgOriginalUnitIds, setOrgOriginalUnitIds] = useState([]);
  const [contactsData, setContactsData] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactsError, setContactsError] = useState('');
  const [showContactFormModal, setShowContactFormModal] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [contactFormData, setContactFormData] = useState(EMPTY_CONTACT_FORM);
  const [contactFormError, setContactFormError] = useState('');
  const [quotedTotalsByOrg, setQuotedTotalsByOrg] = useState({});
  const [detailOrganization, setDetailOrganization] = useState(null);
  const [detailUnits, setDetailUnits] = useState([]);
  const [detailUnitsLoading, setDetailUnitsLoading] = useState(false);
  const [detailQuotations, setDetailQuotations] = useState([]);
  const [detailSales, setDetailSales] = useState([]);
  const [detailCommercialLoading, setDetailCommercialLoading] = useState(false);
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [leadEnrichment, setLeadEnrichment] = useState({
    units_by_org: {},
    last_executive_by_org: {},
    payment_terms_by_org: {},
    total_subunits: 0,
  });

  const executiveOptions = [
    { value: '', label: 'Seleccionar responsable' },
    ...usersData.map((user) => ({
      value: String(user.id),
      label: user.full_name || user.user_code || `USR ${user.id}`,
    })),
  ];

  const loadOrganizations = async () => {
    setOrganizationsLoading(true);
    setOrganizationsError('');
    try {
      const response = await authFetch(`${API_BASE_URL}/api/organizations/`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const rawData = await response.json();
      const normalizedData = rawData.map((row) => ({
        id: row.id,
        org_code: row.org_code || '',
        rut: formatRut(row.rut || ''),
        org_name: row.org_name || '',
        segment: row.segment || '',
        channel: row.channel || '',
        industry: row.industry || '',
        subindustry: row.subindustry || '',
        website: row.website || '',
        linkedin: row.linkedin || '',
        phone: row.phone || '',
        email: row.email || '',
        address: row.address || '',
        commune: row.commune || '',
        region: row.region || '',
        country: row.country || '',
        subunit: row.subunit || '',
        created_by: row.created_by || '',
        assigned_user_id: row.assigned_user_id ?? null,
        assignee_name: row.assignee_name || '',
        full_address: [row.address, row.commune, row.region, row.country].filter(Boolean).join(', '),
        contacts_count: row.contacts_count ?? 0,
        sales_vs_quotes: `${row.sales_count ?? 0}/${row.quotations_count ?? 0}`,
        status: row.status || '',
        sales_amount: Number(row.sales_amount || 0),
        sales_count: row.sales_count ?? 0,
        quotations_count: row.quotations_count ?? 0,
        created_at: row.created_at || '',
      }));
      normalizedData.sort((a, b) => String(a.org_name || '').localeCompare(String(b.org_name || ''), 'es', { sensitivity: 'base' }));
      setOrganizationsData(normalizedData);
      try {
        const er = await authFetch(`${API_BASE_URL}/api/organizations/enrichment/lead-table`);
        if (er.ok) {
          const extra = await er.json();
          setLeadEnrichment({
            units_by_org: extra.units_by_org || {},
            last_executive_by_org: extra.last_executive_by_org || {},
            payment_terms_by_org: extra.payment_terms_by_org || {},
            total_subunits: Number(extra.total_subunits ?? 0),
          });
        } else {
          setLeadEnrichment({
            units_by_org: {},
            last_executive_by_org: {},
            payment_terms_by_org: {},
            total_subunits: 0,
          });
        }
      } catch (e) {
        setLeadEnrichment({
          units_by_org: {},
          last_executive_by_org: {},
          payment_terms_by_org: {},
          total_subunits: 0,
        });
      }
    } catch (error) {
      setOrganizationsError('No se pudo cargar organizaciones desde la base de datos.');
      setOrganizationsData([]);
      setLeadEnrichment({
        units_by_org: {},
        last_executive_by_org: {},
        payment_terms_by_org: {},
        total_subunits: 0,
      });
      console.error('Error loading organizations:', error);
    } finally {
      setOrganizationsLoading(false);
    }
  };

  const handleBulkUpdateOrganizations = async (payload) => {
    const { organizationIds, ...rest } = payload;
    const body = { organization_ids: organizationIds };
    Object.entries(rest).forEach(([k, v]) => {
      if (v !== undefined) body[k] = v;
    });
    const response = await authFetch(`${API_BASE_URL}/api/organizations/bulk-update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const err = await response.json();
        const d = err.detail;
        if (d != null) {
          message = Array.isArray(d) ? d.map((x) => x.msg || String(x)).join(' ') : String(d);
        }
      } catch (_) {
        /* ignore */
      }
      throw new Error(message);
    }
    await loadOrganizations();
  };

  const handleBulkDeleteOrganizations = async (organizationIds) => {
    const response = await authFetch(`${API_BASE_URL}/api/organizations/bulk-delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_ids: organizationIds }),
    });
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const err = await response.json();
        const d = err.detail;
        if (d != null) {
          message = Array.isArray(d) ? d.map((x) => x.msg || String(x)).join(' ') : String(d);
        }
      } catch (_) {
        /* ignore */
      }
      throw new Error(message);
    }
    setDetailOrganization((prev) =>
      prev && organizationIds.map(Number).includes(Number(prev.id)) ? null : prev
    );
    await loadOrganizations();
    await loadContacts();
  };

  const handleBulkImportOrganizations = async (rows) => {
    setIsImportingOrgs(true);
    setOrgBulkImportResult(null);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/organizations/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizations: (Array.isArray(rows) ? rows : []).map((row) => ({
            ...row,
            rut: formatRut(row.rut || ''),
            org_name: normalizeProperCaseText(row.org_name || ''),
          })),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const d = data?.detail;
        const message = Array.isArray(d)
          ? d.map((x) => x.msg || String(x)).join(' ')
          : String(d || `HTTP ${response.status}`);
        throw new Error(message);
      }
      setOrgBulkImportResult(data);
      await loadOrganizations();
      const created = Number(data?.created || 0);
      const skipped = Number(data?.skipped || 0);
      const errors = Number(data?.errors || 0);
      if (errors === 0 || created > 0) {
        setOrgBulkImportNotice(
          `Importación exitosa: ${created} organización(es) creadas` +
            (skipped ? `, ${skipped} omitidas` : '') +
            '.'
        );
      }
      return data;
    } catch (error) {
      const fail = {
        created: 0,
        skipped: 0,
        errors: 1,
        error_rows: [{ row: 0, detail: error.message || 'No se pudo importar.' }],
      };
      setOrgBulkImportResult(fail);
      throw error;
    } finally {
      setIsImportingOrgs(false);
    }
  };

  const loadContacts = async () => {
    setContactsLoading(true);
    setContactsError('');
    try {
      const response = await authFetch(`${API_BASE_URL}/api/contacts/`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const rawData = await response.json();
      const sorted = (Array.isArray(rawData) ? rawData : []).slice().sort((a, b) =>
        String(a.full_name || '').localeCompare(String(b.full_name || ''), 'es', { sensitivity: 'base' })
      );
      setContactsData(sorted);
    } catch (error) {
      setContactsData([]);
      setContactsError('No se pudo cargar contactos desde la base de datos.');
      console.error('Error loading contacts:', error);
    } finally {
      setContactsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/users/`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const rawData = await response.json();
      setUsersData(rawData);
    } catch (error) {
      setUsersData([]);
      console.error('Error loading users:', error);
    }
  };

  useEffect(() => {
    loadOrganizations();
    loadContacts();
    loadUsers();
  }, []);

  useEffect(() => {
    if (!detailOrganization?.id) {
      setDetailUnits([]);
      setDetailUnitsLoading(false);
      return;
    }
    let cancelled = false;
    setDetailUnitsLoading(true);
    (async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/api/organizations/${detailOrganization.id}/units/`);
        const data = response.ok ? await response.json() : [];
        if (!cancelled) setDetailUnits(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setDetailUnits([]);
      } finally {
        if (!cancelled) setDetailUnitsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch, detailOrganization?.id]);

  useEffect(() => {
    if (!detailOrganization?.id) {
      setDetailQuotations([]);
      setDetailSales([]);
      setDetailCommercialLoading(false);
      return;
    }
    let cancelled = false;
    setDetailCommercialLoading(true);
    (async () => {
      try {
        const [quotationsResponse, salesResponse] = await Promise.all([
          authFetch(`${API_BASE_URL}/api/quotations/`),
          authFetch(`${API_BASE_URL}/api/quotations/sales`),
        ]);
        const quotationsRaw = quotationsResponse.ok ? await quotationsResponse.json() : [];
        const salesRaw = salesResponse.ok ? await salesResponse.json() : [];

        const orgId = Number(detailOrganization.id);
        const quotationsList = (Array.isArray(quotationsRaw) ? quotationsRaw : []).filter(
          (q) => Number(q.organization_id) === orgId
        );
        const quotationIds = new Set(quotationsList.map((q) => Number(q.id)));
        const salesList = (Array.isArray(salesRaw) ? salesRaw : []).filter((s) =>
          quotationIds.has(Number(s.quotation_id))
        );

        if (!cancelled) {
          setDetailQuotations(quotationsList);
          setDetailSales(salesList);
        }
      } catch (error) {
        if (!cancelled) {
          setDetailQuotations([]);
          setDetailSales([]);
        }
      } finally {
        if (!cancelled) setDetailCommercialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch, detailOrganization?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/api/quotations/`);
        if (!response.ok) return;
        const list = await response.json();
        const map = {};
        (Array.isArray(list) ? list : []).forEach((q) => {
          const oid = Number(q.organization_id);
          if (!oid) return;
          map[oid] = (map[oid] || 0) + Number(q.total_amount || 0);
        });
        if (!cancelled) setQuotedTotalsByOrg(map);
      } catch (e) {
        if (!cancelled) setQuotedTotalsByOrg({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  const availableYears = useMemo(
    () => getAvailableYears(organizationsData, contactsData),
    [organizationsData, contactsData]
  );

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        organizations: organizationsData,
        contacts: contactsData,
        secondaryYear,
        secondaryMonth,
      }),
    [periodFilter, organizationsData, contactsData, secondaryYear, secondaryMonth]
  );

  const selectedPeriodRange = selectedPeriod.range;
  const selectedPeriodColumns = selectedPeriod.columns;

  useEffect(() => {
    if (!availableYears.length) return;
    if (periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral') {
      if (secondaryYear !== 'all' && !availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear('all');
      }
      return;
    }
    if (periodFilter === 'monthly') {
      if (!availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear(String(availableYears[availableYears.length - 1]));
      }
    }
  }, [availableYears, periodFilter, secondaryYear]);

  const handlePeriodFilterChange = (nextPeriod) => {
    setPeriodFilter(nextPeriod);
    const now = new Date();
    const latestYear = availableYears[availableYears.length - 1] || now.getFullYear();
    if (nextPeriod === 'annual' || nextPeriod === 'annual_ytd' || nextPeriod === 'quarterly' || nextPeriod === 'semestral') {
      setSecondaryYear('all');
      return;
    }
    if (nextPeriod === 'monthly') {
      setSecondaryYear(String(availableYears.includes(now.getFullYear()) ? now.getFullYear() : latestYear));
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  const filteredOrganizationsData = useMemo(
    () => organizationsData.filter((organization) => isDateInRange(organization.created_at, selectedPeriodRange)),
    [organizationsData, selectedPeriodRange]
  );

  const leadsRegionChartItems = useMemo(
    () =>
      organizationsData
        .filter((organization) => isDateInRange(organization.created_at, selectedPeriodRange))
        .map((organization) => ({
          id: organization.id,
          organizationId: organization.id,
          region: organization.region,
          segment: organization.segment,
          status: String(organization.status || '').trim() || 'Sin estado',
          date: organization.created_at,
        })),
    [organizationsData, selectedPeriodRange]
  );

  const leadsCumulativeChartItems = useMemo(
    () =>
      organizationsData
        .filter((organization) => {
          const date = parseDateSafe(organization.created_at);
          return date && date <= selectedPeriodRange.end;
        })
        .map((organization) => ({
          id: organization.id,
          organizationId: organization.id,
          region: organization.region,
          segment: organization.segment,
          status: String(organization.status || '').trim() || 'Sin estado',
          date: organization.created_at,
        })),
    [organizationsData, selectedPeriodRange]
  );

  const leadsChartPeriods = useMemo(() => {
    if (selectedPeriod?.columns?.length) {
      return selectedPeriod.columns;
    }
    const range = selectedPeriod?.range;
    if (range?.start && range?.end) {
      return getMonthsInRange(range.start, range.end);
    }
    return [];
  }, [selectedPeriod]);

  const leadsChartStackDimensions = useMemo(
    () => [
      SEGMENT_STACK_DIMENSION,
      {
        key: 'status',
        label: 'Estado',
        order: ['Lead', 'Calificado', 'Cliente', 'Recurrente'],
        colors: {
          Lead: '#d97706',
          Calificado: '#8b5cf6',
          Cliente: '#0066cc',
          Recurrente: '#0f766e',
        },
        fallback: 'Sin estado',
      },
    ],
    []
  );

  const filteredContactsData = useMemo(
    () => contactsData.filter((contact) => isDateInRange(contact.created_at, selectedPeriodRange)),
    [contactsData, selectedPeriodRange]
  );

  const organizationsTableRows = useMemo(() => {
    return filteredOrganizationsData.map((org) => {
      const orgContacts = contactsData
        .filter((c) => Number(c.organization_id) === Number(org.id))
        .sort((a, b) => Number(a.id) - Number(b.id));
      const contactNames = orgContacts.slice(0, 2).map((c) => c.full_name);
      const uid = String(org.id);
      const primaryUnitNames = leadEnrichment.units_by_org?.[uid] || [];
      const lastExecutiveName = leadEnrichment.last_executive_by_org?.[uid] || '';
      const paymentTerms = leadEnrichment.payment_terms_by_org?.[uid] || '';
      return {
        ...org,
        contactNames,
        quoted_total: quotedTotalsByOrg[Number(org.id)] || 0,
        primary_unit_names: primaryUnitNames,
        last_executive_name: lastExecutiveName,
        payment_terms: paymentTerms,
      };
    });
  }, [filteredOrganizationsData, contactsData, quotedTotalsByOrg, leadEnrichment]);

  const openCreateOrganizationForm = () => {
    setEditingOrgId(null);
    setOrgFormError('');
    setOrgOriginalContactIds([]);
    setOrgOriginalUnitIds([]);
    setOrgFormData({ ...EMPTY_ORG_FORM, country: unit === 'aditivos' ? '' : 'Chile' });
    setShowOrgFormModal(true);
  };

  const openEditOrganizationForm = async (organizationRow) => {
    setEditingOrgId(organizationRow.id);
    setOrgFormError('');
    let loadedUnits = [];
    try {
      const ur = await authFetch(`${API_BASE_URL}/api/organizations/${organizationRow.id}/units/`);
      if (ur.ok) {
        const rawUnits = await ur.json();
        loadedUnits = Array.isArray(rawUnits) ? rawUnits : [];
      }
    } catch (e) {
      loadedUnits = [];
    }
    setOrgFormData({
      org_code: organizationRow.org_code || '',
      org_name: organizationRow.org_name || '',
      rut: formatRut(organizationRow.rut || ''),
      segment: organizationRow.segment || '',
      channel: organizationRow.channel || '',
      created_by: organizationRow.created_by || '',
      assigned_user_id: organizationRow.assigned_user_id
        ? String(organizationRow.assigned_user_id)
        : '',
      website: organizationRow.website || '',
      linkedin: organizationRow.linkedin || '',
      industry: organizationRow.industry || '',
      subindustry: organizationRow.subindustry || '',
      email: organizationRow.email || '',
      phone: organizationRow.phone || '',
      address: organizationRow.address || '',
      commune: organizationRow.commune || '',
      region: organizationRow.region || '',
      country: organizationRow.country || (unit === 'aditivos' ? '' : 'Chile'),
      units: loadedUnits.map((u) => ({
        id: u.id,
        unit_name: u.unit_name || '',
        unit_code: u.unit_code || '',
        address: u.address || '',
        commune: u.commune || '',
        region: u.region || '',
        country: u.country || '',
        notes: u.notes || '',
      })),
      contacts: contactsData
        .filter((contact) => Number(contact.organization_id) === Number(organizationRow.id))
        .map((contact) => ({
          id: contact.id,
          full_name: contact.full_name || '',
          role: contact.role || '',
          email: contact.email || '',
          phone: contact.phone || '',
          linkedin: contact.linkedin || '',
          status: contact.status || 'Activo',
        })),
    });
    setOrgOriginalUnitIds(
      loadedUnits.map((u) => Number(u.id)).filter((id) => Number.isFinite(id))
    );
    setOrgOriginalContactIds(
      contactsData
        .filter((contact) => Number(contact.organization_id) === Number(organizationRow.id))
        .map((contact) => Number(contact.id))
        .filter((id) => Number.isFinite(id))
    );
    setShowOrgFormModal(true);
  };

  const handleOrgFormChange = (field, value) => {
    if (field === 'rut') {
      setOrgFormData((prev) => ({ ...prev, rut: formatRut(value) }));
      return;
    }
    setOrgFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveOrganization = async () => {
    if (!orgFormData.org_name.trim()) {
      setOrgFormError('El nombre de la organización es obligatorio.');
      return;
    }
    const newNameNormalized = normalizeOrgName(orgFormData.org_name);
    const duplicatedExact = organizationsData.find(
      (organization) =>
        Number(organization.id) !== Number(editingOrgId || 0) &&
        normalizeOrgName(organization.org_name) === newNameNormalized
    );
    if (duplicatedExact) {
      setOrgFormError(`Ya existe una organización con ese nombre: ${duplicatedExact.org_name}`);
      return;
    }

    setIsSavingOrg(true);
    setOrgFormError('');

    const payload = {
      org_name: normalizeProperCaseText(orgFormData.org_name.trim()),
      rut: formatRut(orgFormData.rut).trim(),
      segment: orgFormData.segment.trim(),
      channel: serializeOrganizationChannels(orgFormData.channel),
      created_by: orgFormData.created_by.trim() || 'system',
      assigned_user_id: orgFormData.assigned_user_id
        ? Number(orgFormData.assigned_user_id)
        : null,
      website: orgFormData.website.trim(),
      linkedin: orgFormData.linkedin.trim(),
      industry: orgFormData.industry.trim(),
      subindustry: orgFormData.subindustry.trim(),
      email: orgFormData.email.trim(),
      phone: orgFormData.phone.trim(),
      address: normalizeProperCaseText(orgFormData.address.trim()),
      commune: orgFormData.commune.trim(),
      region: orgFormData.region.trim(),
      country: orgFormData.country.trim(),
    };

    const contactsPayload = (orgFormData.contacts || [])
      .map((contact) => ({
        id: contact.id ? Number(contact.id) : null,
        full_name: normalizeProperCaseText(String(contact.full_name || '').trim()),
        role: normalizeProperCaseText(String(contact.role || '').trim()),
        email: String(contact.email || '').trim(),
        phone: String(contact.phone || '').trim(),
        linkedin: String(contact.linkedin || '').trim(),
        status: String(contact.status || 'Activo').trim() || 'Activo',
      }))
      .filter((contact) => contact.full_name);

    const endpoint = editingOrgId
      ? `${API_BASE_URL}/api/organizations/${editingOrgId}`
      : `${API_BASE_URL}/api/organizations/`;
    const method = editingOrgId ? 'PUT' : 'POST';

    try {
      const response = await authFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      const savedOrganization = await response.json();
      const organizationId = Number(editingOrgId || savedOrganization?.id);
      if (!organizationId) {
        throw new Error('No se pudo determinar el ID de la organización para guardar contactos.');
      }

      const currentContactIds = new Set();
      const contactOperations = [];
      contactsPayload.forEach((contact) => {
        const contactPayload = {
          full_name: contact.full_name,
          role: contact.role,
          email: contact.email,
          phone: contact.phone,
          linkedin: contact.linkedin,
          organization_id: organizationId,
          status: contact.status,
        };
        if (contact.id) {
          currentContactIds.add(Number(contact.id));
          contactOperations.push(() =>
            authFetch(`${API_BASE_URL}/api/contacts/${contact.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(contactPayload),
            })
          );
        } else {
          contactOperations.push(() =>
            authFetch(`${API_BASE_URL}/api/contacts/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(contactPayload),
            })
          );
        }
      });

      const deletedContactIds = orgOriginalContactIds.filter((id) => !currentContactIds.has(Number(id)));
      deletedContactIds.forEach((contactId) => {
        contactOperations.push(() => authFetch(`${API_BASE_URL}/api/contacts/${contactId}`, { method: 'DELETE' }));
      });

      for (const operation of contactOperations) {
        const contactResponse = await operation();
        if (!contactResponse.ok) {
          const errorData = await contactResponse.json().catch(() => ({}));
          throw new Error(
            errorData.detail ||
              `La organización fue guardada, pero falló la sincronización de contactos (HTTP ${contactResponse.status}).`
          );
        }
      }

      const unitsPayload = (orgFormData.units || [])
        .map((u) => ({
          id: u.id ? Number(u.id) : null,
          unit_name: normalizeProperCaseText(String(u.unit_name || '').trim()),
          unit_code: String(u.unit_code || '').trim(),
          address: normalizeProperCaseText(String(u.address || '').trim()),
          commune: String(u.commune || '').trim(),
          region: String(u.region || '').trim(),
          country: String(u.country || '').trim(),
          notes: normalizeProperCaseText(String(u.notes || '').trim()),
        }))
        .filter((u) => u.unit_name);

      const currentUnitIds = new Set();
      const unitOperations = [];
      unitsPayload.forEach((u) => {
        const unitBody = {
          unit_name: u.unit_name,
          unit_code: u.unit_code,
          address: u.address,
          commune: u.commune,
          region: u.region,
          country: u.country,
          notes: u.notes,
          sort_order: 0,
        };
        if (u.id) {
          currentUnitIds.add(Number(u.id));
          unitOperations.push(() =>
            authFetch(`${API_BASE_URL}/api/organizations/${organizationId}/units/${u.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(unitBody),
            })
          );
        } else {
          unitOperations.push(() =>
            authFetch(`${API_BASE_URL}/api/organizations/${organizationId}/units/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(unitBody),
            })
          );
        }
      });

      const deletedUnitIds = orgOriginalUnitIds.filter((id) => !currentUnitIds.has(Number(id)));
      deletedUnitIds.forEach((unitId) => {
        unitOperations.push(() =>
          authFetch(`${API_BASE_URL}/api/organizations/${organizationId}/units/${unitId}`, { method: 'DELETE' })
        );
      });

      for (const operation of unitOperations) {
        const unitResponse = await operation();
        if (!unitResponse.ok) {
          throw new Error('La organización fue guardada, pero hubo errores al sincronizar subunidades.');
        }
      }

      setShowOrgFormModal(false);
      setEditingOrgId(null);
      setOrgOriginalContactIds([]);
      setOrgOriginalUnitIds([]);
      setOrgFormData({ ...EMPTY_ORG_FORM });
      await loadOrganizations();
      await loadContacts();
    } catch (error) {
      setOrgFormError(error.message || 'No se pudo guardar la organización.');
    } finally {
      setIsSavingOrg(false);
    }
  };

  const organizationOptionsForContacts = useMemo(
    () => [
      { value: '', label: 'Sin organización' },
      ...organizationsData.map((organization) => ({
        value: String(organization.id),
        label: organization.org_name || `ORG ${organization.id}`,
      })),
    ],
    [organizationsData]
  );

  const openCreateContactForm = () => {
    setEditingContactId(null);
    setContactFormError('');
    setContactFormData({ ...EMPTY_CONTACT_FORM });
    setShowContactFormModal(true);
  };

  const openEditContactForm = (rowToEdit) => {
    if (!rowToEdit) return;
    setEditingContactId(rowToEdit.id);
    setContactFormError('');
    setContactFormData({
      contact_code: rowToEdit.contact_code || '',
      full_name: rowToEdit.full_name || '',
      role: rowToEdit.role || '',
      email: rowToEdit.email || '',
      linkedin: rowToEdit.linkedin || '',
      phone: rowToEdit.phone || '',
      organization_id:
        rowToEdit.organization_id && String(rowToEdit.organization_name || '').trim()
          ? String(rowToEdit.organization_id)
          : '',
      status: rowToEdit.status || 'Activo',
    });
    setShowContactFormModal(true);
  };

  const handleContactFormChange = (field, value) => {
    setContactFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveContact = async () => {
    if (!String(contactFormData.full_name || '').trim()) {
      setContactFormError('El nombre del contacto es obligatorio.');
      return;
    }

    setIsSavingContact(true);
    setContactFormError('');

    const payload = {
      full_name: normalizeProperCaseText(String(contactFormData.full_name || '').trim()),
      role: normalizeProperCaseText(String(contactFormData.role || '').trim()),
      email: String(contactFormData.email || '').trim(),
      linkedin: String(contactFormData.linkedin || '').trim(),
      phone: String(contactFormData.phone || '').trim(),
      organization_id: Number(contactFormData.organization_id || 0),
      status: contactFormData.status || 'Activo',
    };

    const endpoint = editingContactId
      ? `${API_BASE_URL}/api/contacts/${editingContactId}`
      : `${API_BASE_URL}/api/contacts/`;
    const method = editingContactId ? 'PUT' : 'POST';

    try {
      const response = await authFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail;
        const message = Array.isArray(detail)
          ? detail.map((x) => x.msg || String(x)).join(' ')
          : detail || `HTTP ${response.status}`;
        throw new Error(message);
      }
      setShowContactFormModal(false);
      setEditingContactId(null);
      setContactFormData({ ...EMPTY_CONTACT_FORM });
      await loadContacts();
      await loadOrganizations();
    } catch (error) {
      setContactFormError(error.message || 'No se pudo guardar el contacto.');
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleBulkDeleteContacts = async (contactIds) => {
    const ids = (Array.isArray(contactIds) ? contactIds : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) return;
    for (const id of ids) {
      const response = await authFetch(`${API_BASE_URL}/api/contacts/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error('No se pudieron eliminar todos los contactos seleccionados.');
      }
    }
    await loadContacts();
    await loadOrganizations();
  };

  const leadsTabs = [
    {
      key: 'organizations',
      label: 'Organizaciones',
      icon: <Building2 size={16} />,
      content: (
        <Table3
          rows={organizationsTableRows}
          loading={organizationsLoading}
          error={organizationsError}
          isDarkMode={isDarkMode}
          subtitleOverride={`${organizationsTableRows.length} organizaciones creadas en el período seleccionado`}
          onAdd={openCreateOrganizationForm}
          onBulkImport={() => {
            setOrgBulkImportResult(null);
            setOrgBulkImportResult(null);
            setShowOrgBulkImportModal(true);
          }}
          onBulkImportTemplate={downloadOrganizationsExcelTemplate}
          onRowNameClick={(row) => setDetailOrganization(row)}
          onBulkUpdate={handleBulkUpdateOrganizations}
          onBulkDelete={handleBulkDeleteOrganizations}
          bulkExecutiveOptions={executiveOptions}
        />
      ),
    },
    {
      key: 'contacts',
      label: 'Contactos',
      icon: <Users size={16} />,
      content: (
        <ContactsTable3
          rows={filteredContactsData}
          loading={contactsLoading}
          error={contactsError}
          isDarkMode={isDarkMode}
          onAdd={openCreateContactForm}
          onRowNameClick={openEditContactForm}
          onBulkDelete={handleBulkDeleteContacts}
        />
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'start',
          columnGap: '16px',
          rowGap: '12px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Heading level={2} isDarkMode={isDarkMode}>Leads Comerciales</Heading>
          {orgBulkImportNotice ? (
            <div
              style={{
                marginTop: 8,
                padding: '8px 12px',
                borderRadius: 8,
                background: isDarkMode ? 'rgba(34,197,94,0.15)' : '#ecfdf5',
                border: `1px solid ${isDarkMode ? '#166534' : '#86efac'}`,
                color: isDarkMode ? '#86efac' : '#166534',
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'center',
              }}
            >
              <span>{orgBulkImportNotice}</span>
              <button
                type="button"
                onClick={() => setOrgBulkImportNotice('')}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: 'inherit',
                  cursor: 'pointer',
                  fontSize: 16,
                  lineHeight: 1,
                }}
                aria-label="Cerrar aviso"
              >
                ×
              </button>
            </div>
          ) : null}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            gap: '10px',
            flexWrap: 'nowrap',
            marginTop: '22px',
            justifySelf: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
            <span style={periodLabelStyle(ui)}>
              Período
            </span>
            <select
              value={periodFilter}
              onChange={(event) => handlePeriodFilterChange(event.target.value)}
              style={{ ...periodSelectStyle(ui), minWidth: '180px' }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>
                Año
              </span>
              <select
                value={secondaryYear}
                onChange={(event) => setSecondaryYear(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: '110px' }}
              >
                <option value="all">Todas</option>
                {availableYears.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {periodFilter === 'monthly' ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>
                  Año
                </span>
                <select
                  value={String(secondaryYear === 'all' ? availableYears[availableYears.length - 1] || new Date().getFullYear() : secondaryYear)}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>
                  Mes
                </span>
                <select
                  value={secondaryMonth}
                  onChange={(event) => setSecondaryMonth(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '12px',
          alignItems: 'start',
          overflow: 'visible',
          padding: '0 0 4px',
        }}
      >
        <RegionSegmentStackedChart
          items={leadsRegionChartItems}
          title="Distribución de leads"
          totalLabel="organizaciones"
          stackDimensions={leadsChartStackDimensions}
          defaultStackKey="segment"
          axisModes={REGION_ONLY_AXIS_MODES}
        />
        <MonthlyDistributionChart
          periods={leadsChartPeriods}
          items={leadsCumulativeChartItems}
          title="Acumulado de leads"
          totalLabel="organizaciones"
          stackDimensions={leadsChartStackDimensions}
          defaultStackKey="segment"
          axisModes={[{ key: 'month', label: selectedPeriod?.mode === 'year' ? 'Año' : 'Mes' }]}
          cumulative
        />
      </div>
      <LeadsMonthlySummaryTable
        organizations={organizationsData}
        contacts={contactsData}
        periods={selectedPeriodColumns}
        title={selectedPeriod.title}
        collapsed={summaryCollapsed}
        onToggleCollapsed={() => setSummaryCollapsed((prev) => !prev)}
      />
      <TabNavigation tabs={leadsTabs} defaultTab="organizations" isDarkMode={isDarkMode} />
      <OrganizationsForm
        isOpen={showOrgFormModal}
        onClose={() => setShowOrgFormModal(false)}
        onSave={handleSaveOrganization}
        isSaving={isSavingOrg}
        isDarkMode={isDarkMode}
        isEditing={Boolean(editingOrgId)}
        formData={orgFormData}
        onChange={handleOrgFormChange}
        formError={orgFormError}
        executiveOptions={executiveOptions}
        existingOrganizations={organizationsData}
        currentOrganizationId={editingOrgId}
      />
      <OrganizationsBulkImportModal
        isOpen={showOrgBulkImportModal}
        onClose={() => {
          if (isImportingOrgs) return;
          setShowOrgBulkImportModal(false);
          setOrgBulkImportResult(null);
        }}
        onImport={handleBulkImportOrganizations}
        isImporting={isImportingOrgs}
        isDarkMode={isDarkMode}
        result={orgBulkImportResult}
        executiveOptions={executiveOptions}
      />
      <ContactsForm
        isOpen={showContactFormModal}
        onClose={() => setShowContactFormModal(false)}
        onSave={handleSaveContact}
        isSaving={isSavingContact}
        isDarkMode={isDarkMode}
        isEditing={Boolean(editingContactId)}
        formData={contactFormData}
        onChange={handleContactFormChange}
        formError={contactFormError}
        organizationOptions={organizationOptionsForContacts}
      />
      <OrganizationDetailModal
        isOpen={Boolean(detailOrganization)}
        onClose={() => setDetailOrganization(null)}
        organization={detailOrganization}
        contacts={
          detailOrganization
            ? contactsData.filter((c) => Number(c.organization_id) === Number(detailOrganization.id))
            : []
        }
        units={detailUnits}
        unitsLoading={detailUnitsLoading}
        quotations={detailQuotations}
        sales={detailSales}
        commercialLoading={detailCommercialLoading}
        quotedTotal={detailOrganization ? quotedTotalsByOrg[Number(detailOrganization.id)] ?? detailOrganization.quoted_total ?? 0 : 0}
        isDarkMode={isDarkMode}
        onEdit={() => {
          if (!detailOrganization) return;
          const row = detailOrganization;
          setDetailOrganization(null);
          openEditOrganizationForm(row);
        }}
      />
    </div>
  );
};

export default PaintLeadsWorkspacePlaceholder;
